import { create } from 'zustand';
import type { CreateDiscountRequest, DiscountDto, UpdateDiscountRequest } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Discount codes.
 *
 * Codes are switched off, never deleted — a code may already be printed on a
 * flyer or sitting in a customer's inbox, and deleting it turns a promise into
 * a 404. The API derives a single `state` from dates, usage, and the active
 * flag so a list shows one word instead of four fields to interpret.
 *
 * The list's error and the form's are kept apart: a code refused while being
 * written belongs beside the form, not in a banner over the list behind it.
 */

export interface DiscountFilters {
  search: string;
  /** Only codes switched on and inside their dates. */
  activeOnly: boolean;
}

export const EMPTY_DISCOUNT_FILTERS: DiscountFilters = { search: '', activeOnly: false };

interface DiscountsState {
  items: DiscountDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  saving: boolean;
  /** The list's own trouble: loading, or switching a code off. */
  error: string | null;
  /** Why the open form's save was refused. */
  saveError: string | null;
  filters: DiscountFilters;

  load: (patch?: Partial<DiscountFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  create: (input: CreateDiscountRequest) => Promise<boolean>;
  update: (id: string, input: UpdateDiscountRequest) => Promise<boolean>;
  disable: (id: string) => Promise<boolean>;
  /** Switches a code that was switched off back on — it is accepted again at once. */
  enable: (id: string) => Promise<boolean>;
  clearSaveError: () => void;
}

const PAGE_SIZE = 20;

function listQuery(filters: DiscountFilters) {
  return {
    search: filters.search || undefined,
    // Only `true` means anything to the API; any value it receives reads as true.
    activeOnly: filters.activeOnly ? (true as const) : undefined,
  };
}

export const useDiscountsStore = create<DiscountsState>((set, get) => ({
  items: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  saving: false,
  error: null,
  saveError: null,
  filters: EMPTY_DISCOUNT_FILTERS,

  async load(patch) {
    const filters = { ...get().filters, ...patch };
    set({ loading: true, error: null, filters });

    try {
      const page = await withSession(() => adminApi.discounts.list({ limit: PAGE_SIZE, ...listQuery(filters) }));
      // A slower answer to an older filter must not overwrite a newer one.
      if (get().filters !== filters) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      if (get().filters !== filters) return;
      set({ loading: false, error: errorMessage(error, 'Could not load discounts.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, filters, items } = get();
    if (!nextCursor || loadingMore) return;

    set({ loadingMore: true });

    try {
      const page = await withSession(() =>
        adminApi.discounts.list({ limit: PAGE_SIZE, cursor: nextCursor, ...listQuery(filters) }),
      );
      set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more discounts.') });
    }
  },

  async create(input) {
    set({ saving: true, saveError: null });

    try {
      await withSession(() => adminApi.discounts.create(input));
      set({ saving: false });
      await get().load();
      return true;
    } catch (error) {
      // "That code is taken" is the usual one, and it is said as the API words it.
      set({ saving: false, saveError: errorMessage(error, 'Could not create the code.') });
      return false;
    }
  },

  async update(id, input) {
    set({ saving: true, saveError: null });

    try {
      const updated = await withSession(() => adminApi.discounts.update(id, input));
      set((state) => ({
        saving: false,
        items: state.items.map((row) => (row.id === id ? updated : row)),
      }));
      return true;
    } catch (error) {
      // The API refuses to lower maxUses below what has already been redeemed;
      // its message says so, and it is shown verbatim.
      set({ saving: false, saveError: errorMessage(error, 'Could not save the code.') });
      return false;
    }
  },

  async disable(id) {
    set({ saving: true, error: null });

    try {
      const updated = await withSession(() => adminApi.discounts.disable(id));
      set((state) => ({
        saving: false,
        items: state.items.map((row) => (row.id === id ? updated : row)),
      }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not switch off the code.') });
      return false;
    }
  },

  async enable(id) {
    set({ saving: true, error: null });

    try {
      const updated = await withSession(() => adminApi.discounts.update(id, { isActive: true }));
      set((state) => ({
        saving: false,
        items: state.items.map((row) => (row.id === id ? updated : row)),
      }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not switch the code on.') });
      return false;
    }
  },

  clearSaveError() {
    set({ saveError: null });
  },
}));
